import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import { toast } from "react-toastify";
import * as fasiFrazioniRepartoApi from "api/schedulazione/fasiFrazioniRepartoApi";
import { FiltriFasiFrazioniReparto } from "components/userPreference/userPreferenceSlice";
import { deleteOdPLegacy } from "../produzione/odpLegacySlice";

// ============================================
// INTERFACES
// ============================================

export interface OstacoloPack {
    id: number;
    tipo: string;
    tipo_display: string;
    descrizione: string;
    data_segnalazione: string;
    data_prevista_risoluzione?: string;
}

export interface FaseFrazioneReparto {
    id?: number;
    frazione?: number;
    frazione_display?: string;
    odp?: number; // FK OdPLegacy (via frazione.odp_id) — usato per pulizia store su deleteOdPLegacy
    fase_legacy?: number;
    fase_legacy_display?: string;
    odp_display?: string;
    codice_articolo?: string;
    nome_cliente?: string;
    numero_registrazione?: string;
    quantita?: string;
    quantita_completata?: string;
    reparto?: number;
    reparto_display?: string;
    operatore?: number | null;
    operatore_display?: string | null;
    unita_misura?: string;
    codice_lavorazione?: string;
    descrizione?: string;
    data_inizio_pianificata?: string;
    data_fine_pianificata?: string;
    ore_previste?: string;
    ore_lavorate?: string;
    ore_residue?: string;
    ordinamento?: number;
    note?: string;
    percentuale_avanzamento?: string;
    is_bloccata?: boolean;
    ha_ostacoli_attivi?: boolean;
    ostacoli_pack?: OstacoloPack[];
    errorsStack?: ErrorsStack;
}

export interface FasiFrazioniRepartoList {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: FaseFrazioneReparto[];
    errorsStack: ErrorsStack;
}

export interface FasiFrazioniRepartoState {
    fasiFrazioniRepartoList: FasiFrazioniRepartoList;
    faseFrazioneRepartoCorrenteId?: number;
}

// ============================================
// INITIAL STATE
// ============================================

const initialState: FasiFrazioniRepartoState = {
    fasiFrazioniRepartoList: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: { status: ErrorStatusTypes.OK },
    },
    faseFrazioneRepartoCorrenteId: undefined,
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchFasiFrazioniReparto = createAsyncThunk(
    "fasiFrazioniReparto/fetchFasiFrazioniReparto",
    async (parametri: { repartoId: number; filtri?: FiltriFasiFrazioniReparto }) => {
        return await fasiFrazioniRepartoApi.fetchFasiFrazioniReparto(
            parametri.repartoId,
            {
                page: parametri.filtri?.page,
                numero_record: parametri.filtri?.numero_record,
                search: parametri.filtri?.search,
                solo_ostacoli: parametri.filtri?.solo_ostacoli,
                data_da: parametri.filtri?.data_da,
                data_a: parametri.filtri?.data_a,
            }
        );
    }
);

export const getFaseFrazioneReparto = createAsyncThunk(
    "fasiFrazioniReparto/getFaseFrazioneReparto",
    async (parametri: { repartoId: number; faseFrazioneId: number }) => {
        return await fasiFrazioniRepartoApi.getFaseFrazioneReparto(
            parametri.repartoId,
            parametri.faseFrazioneId
        );
    }
);

export const saveFaseFrazioneOperatore = createAsyncThunk(
    "fasiFrazioniReparto/saveFaseFrazioneOperatore",
    async (parametri: { fase_frazione_id: number; operatore_id: number | null }) => {
        return await fasiFrazioniRepartoApi.saveFaseFrazioneOperatore(
            parametri.fase_frazione_id,
            parametri.operatore_id,
        );
    }
);

// ============================================
// SLICE
// ============================================

export const fasiFrazioniRepartoSlice = createSlice({
    name: "fasiFrazioniReparto",
    initialState,
    reducers: {
        setFaseFrazioneRepartoCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.faseFrazioneRepartoCorrenteId = action.payload;
        },
        resetFasiFrazioniReparto: (state) => {
            state.faseFrazioneRepartoCorrenteId = undefined;
            state.fasiFrazioniRepartoList = initialState.fasiFrazioniRepartoList;
        },
    },
    extraReducers: (builder) => {
        // FETCH (Lista)
        builder.addCase(fetchFasiFrazioniReparto.pending, (state) => {
            state.fasiFrazioniRepartoList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(fetchFasiFrazioniReparto.fulfilled, (state, action) => {
            state.fasiFrazioniRepartoList = action.payload;
            state.fasiFrazioniRepartoList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(fetchFasiFrazioniReparto.rejected, (state, action) => {
            state.fasiFrazioniRepartoList.errorsStack = parseErrorMessage(action.error);
        });

        // GET (Singolo)
        builder.addCase(getFaseFrazioneReparto.pending, (state) => {
            state.fasiFrazioniRepartoList.errorsStack = { status: ErrorStatusTypes.PENDING };
        });
        builder.addCase(getFaseFrazioneReparto.fulfilled, (state, action) => {
            const index = state.fasiFrazioniRepartoList.results.findIndex(
                (f) => f.id === action.payload.id
            );
            if (index !== -1) {
                state.fasiFrazioniRepartoList.results[index] = {
                    ...action.payload,
                    errorsStack: { status: ErrorStatusTypes.OK },
                };
            }
            state.fasiFrazioniRepartoList.errorsStack = { status: ErrorStatusTypes.OK };
        });
        builder.addCase(getFaseFrazioneReparto.rejected, (state, action) => {
            state.fasiFrazioniRepartoList.errorsStack = parseErrorMessage(action.error);
        });

        // SAVE OPERATORE
        builder.addCase(saveFaseFrazioneOperatore.fulfilled, (state, action) => {
            const faseAggiornata: FaseFrazioneReparto = action.payload;
            const index = state.fasiFrazioniRepartoList.results.findIndex(
                (f) => f.id === faseAggiornata.id
            );
            if (index !== -1) {
                state.fasiFrazioniRepartoList.results[index] = {
                    ...state.fasiFrazioniRepartoList.results[index],
                    ...faseAggiornata,
                    errorsStack: { status: ErrorStatusTypes.OK },
                };
            }
        });
        builder.addCase(saveFaseFrazioneOperatore.rejected, (state, action) => {
            state.fasiFrazioniRepartoList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'assegnazione dell'operatore");
        });

        // Pulizia store quando si smette di seguire un OdPLegacy:
        // il BE cancella in cascade le FaseFrazione, qui rimuoviamo le orfane dalla cache.
        builder.addCase(deleteOdPLegacy.fulfilled, (state, action) => {
            const odpId = action.meta.arg.odpLegacyToDelete.id;
            if (odpId == null) return;
            const before = state.fasiFrazioniRepartoList.results.length;
            state.fasiFrazioniRepartoList.results = state.fasiFrazioniRepartoList.results.filter(
                (fase) => fase.odp !== odpId
            );
            state.fasiFrazioniRepartoList.count -= before - state.fasiFrazioniRepartoList.results.length;
        });
    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    setFaseFrazioneRepartoCorrenteId,
    resetFasiFrazioniReparto,
} = fasiFrazioniRepartoSlice.actions;

export const fasiFrazioniRepartoSliceReducer = fasiFrazioniRepartoSlice.reducer;
